import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { EntityIndexLayout, WorkspaceShell } from '@/components/layouts';
import { useIsMobile } from '@/hooks/useIsMobile';
import { CreditMemoListPanel } from '@/components/organisms/credit-memos/CreditMemoListPanel';
import { CreditMemoNewButtonSlot } from '@/components/organisms/credit-memos/CreditMemoNewButtonSlot';
import { CreditMemoTableView } from '@/components/organisms/credit-memos/CreditMemoTableView';
import {
  CREDIT_MEMO_STATUS_FILTERS,
  CREDIT_MEMO_TYPE_FILTERS,
  DEFAULT_CREDIT_MEMO_LIST_FILTERS,
  type CreditMemoListFilters,
} from '@/components/organisms/credit-memos/credit-memo-list-filters';

const NUMBER_FILTER_OPS = ['eq', 'gt', 'lt', 'gte', 'lte'] as const;

export const Route = createFileRoute('/_dashboard/dashboard/credit-memos')({
  validateSearch: (search: Record<string, unknown>): CreditMemoListFilters => {
    const sort = Number(search.sort);
    const page = Number(search.page);
    return {
      q: typeof search.q === 'string' ? search.q : DEFAULT_CREDIT_MEMO_LIST_FILTERS.q,
      type: CREDIT_MEMO_TYPE_FILTERS.some((filter) => filter.value === search.type)
        ? (search.type as CreditMemoListFilters['type'])
        : DEFAULT_CREDIT_MEMO_LIST_FILTERS.type,
      status: CREDIT_MEMO_STATUS_FILTERS.some((filter) => filter.value === search.status)
        ? (search.status as CreditMemoListFilters['status'])
        : DEFAULT_CREDIT_MEMO_LIST_FILTERS.status,
      creditMemoNumber:
        typeof search.creditMemoNumber === 'string'
          ? search.creditMemoNumber
          : DEFAULT_CREDIT_MEMO_LIST_FILTERS.creditMemoNumber,
      invoiceId:
        typeof search.invoiceId === 'string'
          ? search.invoiceId
          : DEFAULT_CREDIT_MEMO_LIST_FILTERS.invoiceId,
      totalOp: NUMBER_FILTER_OPS.includes(search.totalOp as CreditMemoListFilters['totalOp'])
        ? (search.totalOp as CreditMemoListFilters['totalOp'])
        : DEFAULT_CREDIT_MEMO_LIST_FILTERS.totalOp,
      totalValue:
        typeof search.totalValue === 'string'
          ? search.totalValue
          : DEFAULT_CREDIT_MEMO_LIST_FILTERS.totalValue,
      remainingOp: NUMBER_FILTER_OPS.includes(
        search.remainingOp as CreditMemoListFilters['remainingOp'],
      )
        ? (search.remainingOp as CreditMemoListFilters['remainingOp'])
        : DEFAULT_CREDIT_MEMO_LIST_FILTERS.remainingOp,
      remainingValue:
        typeof search.remainingValue === 'string'
          ? search.remainingValue
          : DEFAULT_CREDIT_MEMO_LIST_FILTERS.remainingValue,
      sort:
        Number.isFinite(sort) && sort >= 0
          ? Math.min(Math.floor(sort), 7)
          : DEFAULT_CREDIT_MEMO_LIST_FILTERS.sort,
      page:
        Number.isFinite(page) && page > 0
          ? Math.floor(page)
          : DEFAULT_CREDIT_MEMO_LIST_FILTERS.page,
      dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
      dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
      contactId: typeof search.contactId === 'string' ? search.contactId : undefined,
    };
  },
  component: CreditMemosLayout,
});

function cleanCreditMemoSearch(next: Record<string, unknown>): CreditMemoListFilters {
  if (!next.q) delete next.q;
  if (next.type === DEFAULT_CREDIT_MEMO_LIST_FILTERS.type) delete next.type;
  if (next.status === DEFAULT_CREDIT_MEMO_LIST_FILTERS.status) delete next.status;
  if (!next.creditMemoNumber) delete next.creditMemoNumber;
  if (!next.invoiceId) delete next.invoiceId;
  if (next.totalOp === DEFAULT_CREDIT_MEMO_LIST_FILTERS.totalOp) delete next.totalOp;
  if (!next.totalValue) delete next.totalValue;
  if (next.remainingOp === DEFAULT_CREDIT_MEMO_LIST_FILTERS.remainingOp) delete next.remainingOp;
  if (!next.remainingValue) delete next.remainingValue;
  if (next.sort === DEFAULT_CREDIT_MEMO_LIST_FILTERS.sort) delete next.sort;
  if (next.page === DEFAULT_CREDIT_MEMO_LIST_FILTERS.page) delete next.page;
  if (!next.dateFrom) delete next.dateFrom;
  if (!next.dateTo) delete next.dateTo;
  if (!next.contactId) delete next.contactId;
  return next as unknown as CreditMemoListFilters;
}

function CreditMemosLayout() {
  const { location } = useRouterState();
  const router = useRouter();
  const isMobile = useIsMobile();
  const filters = Route.useSearch();

  const idMatch = location.pathname.match(/\/dashboard\/credit-memos\/([^/]+)/);
  const selectedId = idMatch && idMatch[1] !== 'new' ? idMatch[1] : undefined;
  const isNewRoute = location.pathname.includes('/dashboard/credit-memos/new');
  const isFormMode = isNewRoute;
  const hasChildRoute = Boolean(selectedId) || isNewRoute;
  const isIndexRoute = /^\/dashboard\/credit-memos\/?$/.test(location.pathname);

  function handleFiltersChange(patch: Partial<CreditMemoListFilters>) {
    void router.navigate({
      to: '/dashboard/credit-memos',
      search: cleanCreditMemoSearch({ ...filters, ...patch } as Record<string, unknown>),
      replace: true,
    });
  }

  function handleNewCreditMemo() {
    void router.navigate({
      to: '/dashboard/credit-memos/new',
      search: {
        ...DEFAULT_CREDIT_MEMO_LIST_FILTERS,
        ...(filters.contactId ? { contactId: filters.contactId } : {}),
      },
    });
  }

  return (
    <WorkspaceShell hideHeader={isFormMode}>
      <CreditMemoNewButtonSlot
        hidden={isFormMode || (isMobile && hasChildRoute)}
        onClick={handleNewCreditMemo}
      />
      <EntityIndexLayout
        title="Credit Memos"
        storageKey="credit-memos-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() =>
          void router.navigate({ to: '/dashboard/credit-memos', search: filters })
        }
        splitWidthClass="w-[380px]"
        tableSheetWidthClass={
          isFormMode ? 'w-[95vw] sm:max-w-[1000px]' : 'w-[820px] sm:max-w-[820px]'
        }
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        hideListInSplit={isFormMode}
        splitList={() => (
          <div className="flex h-full flex-col">
            <CreditMemoListPanel
              selectedId={selectedId}
              onSelect={(creditMemo) =>
                void router.navigate({
                  to: '/dashboard/credit-memos/$id',
                  params: { id: creditMemo.id },
                  search: filters,
                })
              }
              compact
              filters={filters}
              onFiltersChange={handleFiltersChange}
            />
          </div>
        )}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <CreditMemoTableView
            onOpen={(id) =>
              openInSheet(
                () =>
                  void router.navigate({
                    to: '/dashboard/credit-memos/$id',
                    params: { id },
                    search: filters,
                  }),
              )
            }
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="flex h-full flex-col md:hidden">
            {isIndexRoute ? (
              <CreditMemoListPanel
                selectedId={selectedId}
                onSelect={(creditMemo) =>
                  void router.navigate({
                    to: '/dashboard/credit-memos/$id',
                    params: { id: creditMemo.id },
                    search: filters,
                  })
                }
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            ) : (
              <Outlet />
            )}
          </div>
        }
      />
    </WorkspaceShell>
  );
}
